/**
 * TeamMemberSelect — searchable, alphabetised Team Member picker for Analytics filters.
 * "All Team Members" is pinned first; users are sorted by display name A→Z (case-insensitive);
 * the search box filters by display name. Values stay the raw user ids / 'all'.
 */
import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';

export const sortByDisplayName = (users = []) =>
  [...users].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));

export const TeamMemberSelect = ({ value = 'all', onChange, users = [], allLabel = 'All Team Members', testId = 'hist-user', className = '' }) => {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => sortByDisplayName(users), [users]);
  const selectedLabel = value === 'all' ? allLabel : (sorted.find((u) => u.id === value)?.name || allLabel);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Team Member"
          data-testid={testId}
          className={`flex h-9 w-40 items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${className}`}
        >
          <span className="truncate text-left" data-testid={`${testId}-value`}>{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search team member..." className="h-9" data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>
              <Search className="mx-auto mb-1 h-4 w-4 text-slate-300" />No team members match.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => pick('all')} className="flex items-center gap-2 text-sm font-medium" data-testid={`${testId}-option-all`}>
                <Check className={`h-4 w-4 ${value === 'all' ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                {allLabel}
              </CommandItem>
              {sorted.map((u) => (
                <CommandItem
                  key={u.id}
                  value={u.name || u.id}
                  onSelect={() => pick(u.id)}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`${testId}-option`}
                >
                  <Check className={`h-4 w-4 ${u.id === value ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                  <span className="truncate">{u.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default TeamMemberSelect;
